// src/pages/Documents/DocumentsObligatoiresPage.jsx
import React, { useEffect, useState } from "react";
import { getCoffreFortSegments } from "../../services/segmentDossierServices";
import useScrollToTop from "../../hooks/useScollToTop";
import {
  FaLock,
  FaFilePdf,
  FaFileImage,
  FaFileAlt,
  FaExternalLinkAlt,
  FaInfoCircle,
} from "react-icons/fa";

/* ================= Helpers type de fichier ================= */

function isDriveUrl(url = "") {
  try {
    const u = new URL(url);
    return u.hostname.includes("drive.google.com");
  } catch {
    return false;
  }
}

// Renvoie "pdf" / "image" / "other" en se basant sur l'URL et le nom
function getFileKind(file) {
  const candidates = [file?.lien_fichier, file?.nom_fichier];

  for (const c of candidates) {
    if (!c) continue;
    const clean = c.split("?")[0].toLowerCase();

    if (clean.endsWith(".pdf")) return "pdf";
    if (
      clean.endsWith(".jpg") ||
      clean.endsWith(".jpeg") ||
      clean.endsWith(".png") ||
      clean.endsWith(".gif") ||
      clean.endsWith(".webp") ||
      clean.endsWith(".svg")
    ) {
      return "image";
    }
  }

  return "other";
}

function getFileTypeIcon(file) {
  const kind = getFileKind(file);
  if (kind === "pdf") return FaFilePdf;
  if (kind === "image") return FaFileImage;
  return FaFileAlt;
}

/* ================= Composant principal ================= */

const DocumentsObligatoiresPage = () => {
  useScrollToTop();
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewFile, setPreviewFile] = useState(null); // { nom_fichier, lien_fichier, ... }
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSegments = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getCoffreFortSegments();
        const safe = Array.isArray(data) ? data : [];
        safe.sort((a, b) => (a.ordre_segment || 0) - (b.ordre_segment || 0));
        setSegments(safe);
      } catch (e) {
        console.error(e);
        setError(
          "Impossible de charger les documents obligatoires du club pour le moment."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchSegments();
  }, []);

  const openPreview = (file) => {
    const kind = getFileKind(file);

    // ⚠️ Si c'est un lien Google Drive, on ne tente pas de prévisualisation intégrée
    // (beaucoup de cas sont bloqués par Google). On oblige l'ouverture dans un nouvel onglet.
    if (isDriveUrl(file.lien_fichier)) {
      window.open(file.lien_fichier, "_blank", "noopener,noreferrer");
      return;
    }

    // On ne prévisualise que les images & pdf "classiques"
    if (kind === "image" || kind === "pdf") {
      setPreviewFile(file);
    } else {
      // Pour les autres types, on ouvre simplement dans un nouvel onglet
      window.open(file.lien_fichier, "_blank", "noopener,noreferrer");
    }
  };

  const closePreview = () => {
    setPreviewFile(null);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pt-[130px] sm:pt-[140px] pb-12">
      <div className="mx-auto max-w-6xl px-3 sm:px-6 font-montserrat">
        {/* HERO */}
        <header className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500 text-white shadow-lg mb-8">
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top,_#ffffff55,_transparent_60%)]" />
          <div className="relative px-6 py-8 sm:px-10 sm:py-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="mt-1 flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 backdrop-blur">
                <FaLock className="text-2xl" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
                  Documents obligatoires & informations légales
                </h1>
                <p className="mt-2 text-sm sm:text-[15px] text-white/90 max-w-2xl">
                  Retrouvez ici les diplômes, attestations, assurances et documents de
                  prévention que le club met à disposition du public, conformément aux
                  obligations réglementaires.
                </p>
                <div className="mt-3 inline-flex items-center rounded-full bg-white/15 px-3 py-1 text-xs sm:text-[13px]">
                  <FaInfoCircle className="mr-2" />
                  Certains documents restent affichés physiquement dans le dojo, mais sont
                  également consultables en ligne.
                </div>
              </div>
            </div>

            <div className="relative lg:self-stretch lg:w-64">
              <div className="h-32 sm:h-40 lg:h-full rounded-2xl bg-white/10 border border-white/30 backdrop-blur flex flex-col justify-center px-4">
                <p className="text-xs uppercase tracking-wide text-white/80 mb-1">
                  Transparence & sécurité
                </p>
                <p className="text-sm sm:text-[15px] text-white/95">
                  Le club s’engage pour un encadrement qualifié, une pratique sécurisée et
                  la prévention des violences et du harcèlement.
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* CONTENU PRINCIPAL */}
        {loading && (
          <div className="mt-8 space-y-4">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="h-32 rounded-2xl bg-slate-200/70 dark:bg-slate-800/80 animate-pulse"
              />
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="mt-8 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-800 text-sm">
            {error}
          </div>
        )}

        {!loading && !error && segments.length === 0 && (
          <div className="mt-10 text-center text-slate-600 dark:text-slate-300">
            <p className="font-semibold text-lg">
              Aucun document n’a encore été publié dans cette section.
            </p>
            <p className="text-sm mt-1">
              Les responsables du club pourront prochainement mettre en ligne les documents
              obligatoires et d’information du public.
            </p>
          </div>
        )}

        {!loading &&
          !error &&
          segments.map((segment) => (
            <section
              key={segment.id}
              className="mt-8 rounded-3xl border border-slate-200/80 dark:border-slate-800/80 bg-white/80 dark:bg-slate-900/70 shadow-sm overflow-hidden"
            >
              {/* En-tête de segment */}
              <div className="border-b border-slate-100 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-900/80 px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 h-9 w-9 rounded-2xl bg-blue-500/10 dark:bg-blue-500/20 flex items-center justify-center text-blue-600 dark:text-blue-300">
                    <FaInfoCircle />
                  </div>
                  <div>
                    <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-50">
                      {segment.segment_name}
                    </h2>
                    {segment.segment_content && (
                      <div
                        className="mt-1 text-xs sm:text-sm leading-relaxed text-slate-600 dark:text-slate-300"
                        dangerouslySetInnerHTML={{
                          __html: segment.segment_content,
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Liste de documents */}
              <div className="px-4 sm:px-6 py-4">
                {(!segment.fichiers || segment.fichiers.length === 0) && (
                  <p className="text-sm text-slate-500 dark:text-slate-400 italic">
                    Aucun document n’est disponible dans cette catégorie pour le moment.
                  </p>
                )}

                {segment.fichiers && segment.fichiers.length > 0 && (
                  <div className="grid gap-4 sm:gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                    {segment.fichiers.map((file) => {
                      const kind = getFileKind(file);
                      const Icon = getFileTypeIcon(file);
                      const drive = isDriveUrl(file.lien_fichier);

                      const isPreviewable = !drive && (kind === "image" || kind === "pdf");

                      return (
                        <article
                          key={file.id}
                          className="group relative flex flex-col overflow-hidden rounded-2xl border border-slate-200/80 dark:border-slate-800/80 bg-white/90 dark:bg-slate-950/70 shadow-sm hover:shadow-md transition-shadow"
                        >
                          {/* Vignette */}
                          <button
                            type="button"
                            onClick={() => openPreview(file)}
                            className="relative block w-full aspect-[4/3] overflow-hidden"
                          >
                            {kind === "image" && !drive ? (
                              <img
                                src={file.lien_fichier}
                                alt={file.nom_fichier}
                                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                              />
                            ) : (
                              <div className="h-full w-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center">
                                <Icon className="text-3xl sm:text-4xl text-white mb-2" />
                                <span className="px-2 py-0.5 rounded-full bg-white/10 text-[11px] uppercase tracking-wide text-white/80">
                                  {drive
                                    ? "Ouvrir sur Google Drive"
                                    : kind === "pdf"
                                    ? "Document PDF"
                                    : "Document"}
                                </span>
                              </div>
                            )}

                            {/* Overlay "Voir" */}
                            <div className="pointer-events-none absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                              <span className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-slate-900 shadow">
                                {drive
                                  ? "Ouvrir sur Google Drive"
                                  : isPreviewable
                                  ? "Voir l’aperçu"
                                  : "Ouvrir le document"}
                              </span>
                            </div>
                          </button>

                          {/* Infos */}
                          <div className="flex-1 p-3 sm:p-4 flex flex-col">
                            <h3 className="text-sm sm:text-[15px] font-semibold text-slate-900 dark:text-slate-50 line-clamp-2">
                              {file.nom_fichier}
                            </h3>
                            {file.detail_fichier && (
                              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 line-clamp-3">
                                {file.detail_fichier}
                              </p>
                            )}

                            <div className="mt-3 flex items-center justify-between">
                              <button
                                type="button"
                                onClick={() => openPreview(file)}
                                className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1 bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white transition"
                              >
                                <Icon className="text-[13px]" />
                                {drive
                                  ? "Google Drive"
                                  : isPreviewable
                                  ? "Ouvrir un aperçu"
                                  : "Ouvrir"}
                              </button>
                              <a
                                href={file.lien_fichier}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
                              >
                                <FaExternalLinkAlt className="text-[10px]" />
                                Nouvel onglet
                              </a>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>
          ))}

        {/* Modale d’aperçu (uniquement pour les fichiers non-Drive, image/pdf) */}
        {previewFile && (
          <div
            className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 px-3 sm:px-6"
            onClick={closePreview}
          >
            <div
              className="relative max-w-4xl w-full max-h-[85vh] rounded-2xl bg-slate-950 text-slate-50 shadow-2xl overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
                <div className="flex items-center gap-2">
                  {getFileKind(previewFile) === "image" && (
                    <FaFileImage className="text-lg text-blue-300" />
                  )}
                  {getFileKind(previewFile) === "pdf" && (
                    <FaFilePdf className="text-lg text-rose-300" />
                  )}
                  {getFileKind(previewFile) === "other" && (
                    <FaFileAlt className="text-lg text-slate-200" />
                  )}
                  <div>
                    <h3 className="text-sm sm:text-base font-semibold">
                      {previewFile.nom_fichier}
                    </h3>
                    {previewFile.detail_fichier && (
                      <p className="text-[11px] text-slate-400 line-clamp-1">
                        {previewFile.detail_fichier}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <a
                    href={previewFile.lien_fichier}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded-full border border-slate-600 px-3 py-1 text-[11px] uppercase tracking-wide hover:border-slate-300 hover:text-slate-100"
                  >
                    <FaExternalLinkAlt className="text-[10px]" />
                    Nouvel onglet
                  </a>
                  <button
                    onClick={closePreview}
                    className="rounded-full bg-slate-800 hover:bg-slate-700 px-2.5 py-1 text-xs"
                  >
                    Fermer
                  </button>
                </div>
              </div>

              <div className="flex-1 bg-slate-900 flex items-center justify-center">
                {(() => {
                  const kind = getFileKind(previewFile);

                  if (kind === "image") {
                    return (
                      <img
                        src={previewFile.lien_fichier}
                        alt={previewFile.nom_fichier}
                        className="max-h-[78vh] max-w-full object-contain"
                      />
                    );
                  }

                  if (kind === "pdf") {
                    return (
                      <iframe
                        title={previewFile.nom_fichier}
                        src={previewFile.lien_fichier}
                        className="w-full h-[78vh] border-0"
                      />
                    );
                  }

                  // cas "other" (en théorie on ne devrait pas arriver ici, mais sécurité)
                  return (
                    <div className="p-6 text-center text-sm text-slate-300">
                      <p>
                        Ce document ne peut pas être prévisualisé ici, mais vous pouvez
                        l’ouvrir dans un nouvel onglet.
                      </p>
                      <a
                        href={previewFile.lien_fichier}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-3 inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-4 py-2 text-xs font-semibold"
                      >
                        <FaExternalLinkAlt className="text-[12px]" />
                        Ouvrir le document
                      </a>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentsObligatoiresPage;
